import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PRODUCT_CATEGORY_LABELS_IT,
  PRODUCT_STATUS_LABELS_IT,
  productCreateRequestSchema,
  type ProductCreateRequest,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { ApiError } from '../../lib/api-client';
import { useCreateProduct, useProduct, useUpdateProduct } from '../../lib/products-api';

const DEFAULT_VALUES: ProductCreateRequest = {
  code: '',
  name: '',
  category: 'ALTRO',
  unit: 'g',
  vatRate: 0,
  thcThreshold: null,
  countriesSale: [],
  status: 'ACTIVE',
  notes: null,
};

function joinCountries(arr: string[]): string {
  return arr.join(', ');
}

function parseCountries(input: string): string[] {
  return input
    .split(/[,\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s.length > 0);
}

interface CountriesInputProps {
  value: string[];
  onChange: (v: string[]) => void;
}

function CountriesInput({ value, onChange }: CountriesInputProps) {
  return (
    <Input
      placeholder="IT, CZ, DE, FR"
      defaultValue={joinCountries(value)}
      onBlur={(e) => onChange(parseCountries(e.target.value))}
    />
  );
}

export function ProductFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const productQ = useProduct(id);
  const createMut = useCreateProduct();
  const updateMut = useUpdateProduct(id ?? '');

  const form = useForm<ProductCreateRequest>({
    resolver: zodResolver(productCreateRequestSchema),
    defaultValues: DEFAULT_VALUES,
  });

  // Quando il fetch in edit-mode arriva, popola il form
  useEffect(() => {
    if (isEdit && productQ.data) {
      form.reset({
        code: productQ.data.code,
        name: productQ.data.name,
        category: productQ.data.category,
        unit: productQ.data.unit,
        vatRate: productQ.data.vatRate,
        thcThreshold: productQ.data.thcThreshold,
        countriesSale: productQ.data.countriesSale,
        status: productQ.data.status,
        notes: productQ.data.notes,
      });
    }
  }, [isEdit, productQ.data, form]);

  async function onSubmit(values: ProductCreateRequest) {
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(values);
      } else {
        await createMut.mutateAsync(values);
      }
      navigate('/products');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        form.setError('code', { message: t('products.form.errorDuplicateCode') });
      } else {
        window.alert((err as Error).message);
      }
    }
  }

  const isSubmitting = createMut.isPending || updateMut.isPending;

  if (isEdit && productQ.isLoading) {
    return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  }
  if (isEdit && productQ.error) {
    return (
      <p className="text-sm text-red-600">
        {t('products.form.errorLoad')}: {(productQ.error as Error).message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">
          {isEdit ? t('products.form.titleEdit') : t('products.form.titleNew')}
        </h2>
        <Button variant="secondary" onClick={() => navigate('/products')}>
          {t('common.back')}
        </Button>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>{t('products.form.sectionGeneral')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('products.fields.code')} *</Label>
              <Input id="code" autoComplete="off" {...form.register('code')} />
              {form.formState.errors.code && (
                <p className="text-xs text-red-600">{form.formState.errors.code.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">{t('products.fields.name')} *</Label>
              <Input id="name" autoComplete="off" {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="text-xs text-red-600">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="category">{t('products.fields.category')}</Label>
              <select
                id="category"
                className="block h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                {...form.register('category')}
              >
                {Object.entries(PRODUCT_CATEGORY_LABELS_IT).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status">{t('products.fields.status')}</Label>
              <select
                id="status"
                className="block h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                {...form.register('status')}
              >
                {Object.entries(PRODUCT_STATUS_LABELS_IT).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('products.form.sectionFiscal')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="unit">{t('products.fields.unit')}</Label>
              <Input id="unit" {...form.register('unit')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vatRate">{t('products.fields.vatRate')} (%)</Label>
              <Input
                id="vatRate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...form.register('vatRate', { valueAsNumber: true })}
              />
              {form.formState.errors.vatRate && (
                <p className="text-xs text-red-600">{form.formState.errors.vatRate.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="thcThreshold">{t('products.fields.thcThreshold')} (%)</Label>
              <Input
                id="thcThreshold"
                type="number"
                step="0.0001"
                min="0"
                max="100"
                placeholder={t('products.form.thcPlaceholder')}
                {...form.register('thcThreshold', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              {form.formState.errors.thcThreshold && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.thcThreshold.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('products.form.sectionMarkets')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t('products.fields.countriesSale')}</Label>
              <CountriesInput
                value={form.watch('countriesSale') ?? []}
                onChange={(v) => form.setValue('countriesSale', v, { shouldValidate: true })}
              />
              <p className="text-xs text-slate-500">{t('products.form.countriesHint')}</p>
              {form.formState.errors.countriesSale && (
                <p className="text-xs text-red-600">
                  {form.formState.errors.countriesSale.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">{t('products.fields.notes')}</Label>
              <textarea
                id="notes"
                rows={3}
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                {...form.register('notes', {
                  setValueAs: (v) => (v === '' ? null : v),
                })}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate('/products')}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isEdit ? t('common.save') : t('products.form.createButton')}
          </Button>
        </div>
      </form>
    </div>
  );
}
